import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';
import { ListHourlyBattery } from '../../api/Performance';

// ACTIONS TYPES
export const FETCH_HOURLY_BATTERY_SUCCESS = 'customer-portal/performance/FETCH_HOURLY_BATTERY_SUCCESS';
export const FETCH_HOURLY_BATTERY_FAILURE = 'customer-portal/performance/FETCH_HOURLY_BATTERY_FAILURE';
export const FETCHING_HOURLY_BATTERY = 'customer-portal/performance/FETCHING_HOURLY_BATTERY';

// ACTION CREATORS
export const fetchHourlyBatterySuccess = battery => ({
  type: FETCH_HOURLY_BATTERY_SUCCESS,
  payload: battery,
});

export const fetchHourlyBatteryFailure = (err, startDate, endDate) => ({
  type: FETCH_HOURLY_BATTERY_FAILURE,
  error: err,
  startDate,
  endDate,
});

export const fetchingHourlyBattery = () => ({
  type: FETCHING_HOURLY_BATTERY,
});

// THUNKS
export const fetchHourlyBattery = (prospectId, startDate, endDate) => async dispatch => {
  dispatch(fetchingHourlyBattery());
  try {
    const { data } = await ListHourlyBattery(prospectId, startDate, endDate);
    dispatch(
      fetchHourlyBatterySuccess({
        data,
        prospectId,
        startDate,
        endDate,
      }),
    );
  } catch (error) {
    dispatch(fetchHourlyBatteryFailure(error.message, startDate, endDate));
  }
};

export const shouldFetchHourlyBattery = (hourlyBatteryState, prospectId, startDate, endDate) => {
  if (hourlyBatteryState.get('loading')) {
    return false;
  }

  const readings = hourlyBatteryState.get('readings');
  const existing = readings.filter(
    reading =>
      reading.prospectId === prospectId.toString() &&
      DateTime.local(reading.startDate).hasSame(DateTime.local(startDate), 'minute') &&
      DateTime.local(reading.endDate).hasSame(DateTime.local(endDate), 'minute'),
  );

  return existing.size === 0;
};

export const fetchHourlyBatteryIfNeeded = (prospectId, startDate, endDate) => (dispatch, getState) => {
  if (!shouldFetchHourlyBattery(getState().hourlyBattery, prospectId, startDate, endDate)) {
    return null;
  }
  return dispatch(fetchHourlyBattery(prospectId, startDate, endDate));
};

// SELECTORS
const hourlyBatterySelector = state => state.hourlyBattery;

export const getHourlyBattery = (prospectId, startDate, endDate) =>
  createSelector([hourlyBatterySelector], hourlyBattery => {
    const allReadings = hourlyBattery.get('readings');
    const readings = allReadings.filter(
      reading =>
        reading.prospectId === prospectId.toString() &&
        DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
        DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
    );
    if (readings.size > 0) {
      return readings.first().data.site;
    }
    return null;
  });

export const getHourlyBatteryLoading = createSelector([hourlyBatterySelector], hourlyBattery => hourlyBattery.get('loading') || !hourlyBattery.get('loaded'));

// REDUCER
export const initialState = fromJS({
  loaded: false,
  loading: false,
  error: null,
  readings: [],
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_HOURLY_BATTERY_SUCCESS:
      return state
        .set('loaded', true)
        .set('loading', false)
        .updateIn(['readings'], arr => arr.push(action.payload));
    case FETCH_HOURLY_BATTERY_FAILURE:
      return state.set('loading', false).set('loaded', true).set('error', action.error);
    case FETCHING_HOURLY_BATTERY:
      return state.set('loading', true);
    default:
      return state;
  }
};
